import { IBaseFormElementProps, BaseFormElement } from "./BaseFormElement";
import { IValidationRule } from "./Validation/IValidationRule";
import { Validator } from "./Validation/Validator";
import * as React from "react";
import { ObjectComparer } from "../Utils/ObjectComparer";
import { isNullOrEmpty } from "../Utils/Arrays";
import * as $ from "jquery";

export interface ICheckboxSpecificProps extends IBaseFormElementProps {
    value?: boolean;

    onChange?: (newValue: boolean) => void;

    onValidationResultChange?: (isValid: boolean, validationErrors: string[], showValidationMessages?: boolean) => void;

    disabled?: boolean;

    required?: string;

    id?: string;

    cssInput?: string;

    tabIndex?: number;

    validationRules?: IValidationRule<boolean>[];
}

export interface ICheckboxProps extends ICheckboxSpecificProps, React.Props<Checkbox> {
    // NOTE: leave empty
}

export interface ICheckboxState {
    value?: boolean;

    pristineValue?: boolean;

    isValid?: boolean;

    disable?: boolean;

    isPristine?: boolean;
}

export class Checkbox extends BaseFormElement<ICheckboxProps, ICheckboxState> {
    protected validator: Validator<boolean>;

    protected inputRef: HTMLInputElement;

    constructor(props: ICheckboxProps) {
        super(props);
        this.state = this.getDefaultState(props);

        this.validator = new Validator<boolean>();
        this.initValidation();
    }

    public setDisable() {
        this.setState({ disable: true });
    }

    public render(): JSX.Element {
        const check = this.state.value ? { checked: true } : null;
        return (
            <>
                <input
                    type="checkbox"
                    className={this.props.cssInput || "form-input"}
                    checked={this.state.value}
                    disabled={this.state.disable == true ? true : this.props.disabled}
                    id={this.props.id}
                    onChange={() => this.onChange()}
                    ref={(select: HTMLInputElement) => (this.inputRef = select)}
                    tabIndex={this.props.tabIndex}
                />
                <span aria-hidden="true" className="gfx" />
            </>
        );
    }

    public componentWillReceiveProps(nextProps: ICheckboxProps): void {
        // If the value passed has changed, set it. If value is not passed it will
        // internally update, and this will never run
        if (
            !ObjectComparer.isSame(this.props.value, nextProps.value) &&
            !ObjectComparer.isSame(nextProps.value, this.state.value)
        ) {
            this.setValue(nextProps.value, true);
        }
    }

    public componentDidUpdate(prevProps: ICheckboxProps): void {
        $(`#${this.props.id}`).attr("checked", this.props.value ? "checked" : null);
        if (!ObjectComparer.isSame(this.props.value, prevProps.value) && !this.state.isPristine) {
            this.isValid();
        }
    }

    public shouldComponentUpdate(nextProps: ICheckboxProps, nextState: ICheckboxState): boolean {
        return (
            ObjectComparer.objectsDiffer(this.props, nextProps) || ObjectComparer.objectsDiffer(this.state, nextState)
        );
    }

    public isValid(): boolean {
        const value = this.inputRef.checked;
        return this.runValidation(value);
    }

    protected initValidation(): void {
        this.validator.clear();

        this.validator.add((value: boolean) => value, this.props.required);
        this.validator.addRange(this.props.validationRules);
    }

    protected runValidation(value: boolean): boolean {
        if ($(this.inputRef).is(":hidden")) {
            return true;
        }

        this.initValidation();

        const errors = this.validator.getValidationErrors(value);
        this.triggerValidationResultChange(errors);

        const isValid = isNullOrEmpty(errors);
        this.setState({ isValid });

        return isValid;
    }

    private getDefaultState(props: ICheckboxProps): ICheckboxState {
        return {
            value: props.value,
            isValid: true,
            isPristine: true,
            pristineValue: props.value,
        };
    }

    private setValue(value: boolean, isPristine: boolean): void {
        this.setState({
            value,
            isPristine,
        });
    }

    private onChange(): any {
        const value = this.inputRef.checked;
        this.setValue(value, false);
        if (this.props.onChange) {
            this.runValidation(value);
            this.props.onChange(value);
        }
    }
}
